import type IAIDraftUnitItem from '../../../../../../store/slices/valueChainMap/interfaces/IAIDraftUnitItem';
import type {
  IAIDraftConnection,
  IAIDraftUnit,
} from '../../../../../../store/slices/valueChainMap/interfaces/IAIDraftUnitItem';
import type { IGetValueChainBoardResponse } from '../../../../../../store/slices/valueChainMap/interfaces/IGetValueChainBoardResponse';
import type IDraftBoardUnit from '../interfaces/IDraftBoardUnit';
import type IDraftUnitToDelete from '../interfaces/IDraftUnitToDelete';

const isDraftBoardUnit = (unit: IDraftBoardUnit): boolean => (
  unit.draft === true || typeof unit.draftServerId === 'number'
);

const isDraftBoardUnitMatch = (
  unit: IDraftBoardUnit,
  draftUnitToDelete: Pick<IDraftUnitToDelete, 'id' | 'runtimeId'>,
): boolean => {
  if (!isDraftBoardUnit(unit)) {
    return false;
  }

  if (typeof unit.draftServerId === 'number' && unit.draftServerId === draftUnitToDelete.id) {
    return true;
  }

  if (Number.isInteger(draftUnitToDelete.runtimeId) && unit.id === draftUnitToDelete.runtimeId) {
    return true;
  }

  if (Number.isInteger(draftUnitToDelete.id) && unit.id === draftUnitToDelete.id) {
    return true;
  }

  return false;
};

const isDeletedDraftStructureUnit = (
  draftUnit: IAIDraftUnit | null | undefined,
  deletedDraftUnitIds: Set<number>,
): boolean => (
  typeof draftUnit?.id === 'number' && deletedDraftUnitIds.has(draftUnit.id)
);

const isDraftStructureConnectionRelatedToDeletedUnit = (
  draftConnection: IAIDraftConnection,
  deletedDraftUnitIds: Set<number>,
): boolean => (
  (typeof draftConnection.fromDraftId === 'number' && deletedDraftUnitIds.has(draftConnection.fromDraftId))
  || (typeof draftConnection.toDraftId === 'number' && deletedDraftUnitIds.has(draftConnection.toDraftId))
  || Boolean(draftConnection.fromDraftUnit?.some((draftUnit) => (
    isDeletedDraftStructureUnit(draftUnit, deletedDraftUnitIds)
  )))
  || Boolean(draftConnection.toDraftUnit?.some((draftUnit) => (
    isDeletedDraftStructureUnit(draftUnit, deletedDraftUnitIds)
  )))
);

export const removeDeletedDraftUnitsFromDraftStructure = (
  draftStructure: IAIDraftUnitItem,
  deletedDraftUnitIds: Set<number>,
): IAIDraftUnitItem => {
  if (deletedDraftUnitIds.size === 0) {
    return draftStructure;
  }

  return {
    ...draftStructure,
    draftUnits: draftStructure.draftUnits
      .filter((draftUnit) => !isDeletedDraftStructureUnit(draftUnit, deletedDraftUnitIds))
      .map((draftUnit) => ({
        ...draftUnit,
        draftConnectionToUnits: draftUnit.draftConnectionToUnits?.filter((draftConnection) => (
          !isDraftStructureConnectionRelatedToDeletedUnit(draftConnection, deletedDraftUnitIds)
        )),
      })),
    draftConnections: draftStructure.draftConnections.filter((draftConnection) => (
      !isDraftStructureConnectionRelatedToDeletedUnit(draftConnection, deletedDraftUnitIds)
    )),
  };
};

const collectNestedDraftUnitIds = (
  unitToCollect: IDraftBoardUnit,
  deletedUnitIds: Set<number>,
): void => {
  if (isDraftBoardUnit(unitToCollect)) {
    deletedUnitIds.add(unitToCollect.id);
  }

  unitToCollect.children.forEach((childUnit) => collectNestedDraftUnitIds(childUnit, deletedUnitIds));
};

const collectDraftUnitIds = (
  units: IDraftBoardUnit[],
  draftUnitToDelete: Pick<IDraftUnitToDelete, 'id' | 'runtimeId'>,
  deletedUnitIds: Set<number>,
): void => {
  units.forEach((unit) => {
    const shouldDeleteUnit = isDraftBoardUnitMatch(unit, draftUnitToDelete);

    if (shouldDeleteUnit) {
      collectNestedDraftUnitIds(unit, deletedUnitIds);
      return;
    }

    collectDraftUnitIds(unit.children, draftUnitToDelete, deletedUnitIds);
  });
};

const removeDeletedConnections = (
  unit: IDraftBoardUnit,
  deletedUnitIds: Set<number>,
): IDraftBoardUnit => ({
  ...unit,
  children: unit.children.map((childUnit) => removeDeletedConnections(childUnit, deletedUnitIds)),
  lConToUnits: unit.lConToUnits?.filter((connection) => !deletedUnitIds.has(connection.toId)),
  fConToUnits: unit.fConToUnits?.filter((connection) => !deletedUnitIds.has(connection.toId)),
  gsConToUnits: unit.gsConToUnits?.filter((connection) => !deletedUnitIds.has(connection.toId)),
  sConToUnits: unit.sConToUnits?.filter((connection) => !deletedUnitIds.has(connection.toId)),
  mConToUnits: unit.mConToUnits?.filter((connection) => !deletedUnitIds.has(connection.toId)),
});

const removeDraftUnitFromUnits = (
  units: IDraftBoardUnit[],
  deletedUnitIds: Set<number>,
): IDraftBoardUnit[] => units
  .filter((unit) => !(isDraftBoardUnit(unit) && deletedUnitIds.has(unit.id)))
  .map((unit, index) => {
    const unitWithoutDeletedConnections = removeDeletedConnections(unit, deletedUnitIds);

    return {
      ...unitWithoutDeletedConnections,
      orderIndex: index,
      children: removeDraftUnitFromUnits(unitWithoutDeletedConnections.children, deletedUnitIds),
    };
  });

export const removeDraftUnitFromBoardData = (
  boardData: IGetValueChainBoardResponse | undefined,
  draftUnitToDelete: Pick<IDraftUnitToDelete, 'id' | 'runtimeId'>,
): IGetValueChainBoardResponse | undefined => {
  if (!boardData) {
    return undefined;
  }

  const deletedUnitIds = new Set<number>();

  boardData.data.boards.forEach((board) => {
    board.columns.forEach((column) => {
      collectDraftUnitIds(column.units as IDraftBoardUnit[], draftUnitToDelete, deletedUnitIds);
    });
  });

  if (deletedUnitIds.size === 0) {
    return boardData;
  }

  return {
    ...boardData,
    data: {
      ...boardData.data,
      boards: boardData.data.boards.map((board) => ({
        ...board,
        columns: board.columns.map((column) => ({
          ...column,
          units: removeDraftUnitFromUnits(column.units as IDraftBoardUnit[], deletedUnitIds),
        })),
      })),
    },
  };
};

export const hasDraftUnitsInBoardData = (boardData: IGetValueChainBoardResponse | undefined): boolean => {
  if (!boardData) {
    return false;
  }

  const hasDraftUnits = (units: IDraftBoardUnit[]): boolean => units.some((unit) => (
    isDraftBoardUnit(unit)
    || hasDraftUnits(unit.children)
  ));

  return boardData.data.boards.some((board) => (
    board.columns.some((column) => hasDraftUnits(column.units as IDraftBoardUnit[]))
  ));
};
